/**
 * Admin Service
 * Handles all admin-related API operations
 */

import api from '@services/api';

interface DashboardStats {
  total_users: number;
  active_users: number;
  total_conversations: number;
  total_messages: number;
}

interface User {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  conversation_count?: number;
  message_count?: number;
}

interface CreateUserPayload {
  email: string;
  full_name: string;
  role: 'user' | 'admin';
  password: string;
}

interface UserListResponse {
  users: User[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

interface TenantSettings {
  tenant_prompt: string | null;
}

class AdminService {
  /**
   * Get admin dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const { data } = await api.get('/admin/dashboard');
    return data;
  }

  /**
   * Get all users in tenant
   */
  async getUsers(params?: {
    page?: number;
    per_page?: number;
    role?: string;
    status?: string;
    search?: string;
  }): Promise<UserListResponse> {
    const { data } = await api.get('/admin/users', { params });
    return data;
  }

  /**
   * Create a new user
   */
  async createUser(payload: CreateUserPayload): Promise<User> {
    const { data } = await api.post('/admin/users', payload);
    return data.user;
  }

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: string): Promise<User> {
    const { data } = await api.put(`/admin/users/${userId}/role`, { role });
    return data.user;
  }

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  }

  /**
   * Activate a user
   */
  async activateUser(userId: string): Promise<User> {
    const { data } = await api.post(`/admin/users/${userId}/activate`);
    return data.user;
  }

  /**
   * Deactivate a user
   */
  async deactivateUser(userId: string): Promise<User> {
    const { data } = await api.post(`/admin/users/${userId}/deactivate`);
    return data.user;
  }

  /**
   * Toggle user active status (calls activate or deactivate based on current state)
   */
  async toggleUserStatus(userId: string, currentIsActive: boolean): Promise<User> {
    if (currentIsActive) {
      return this.deactivateUser(userId);
    } else {
      return this.activateUser(userId);
    }
  }

  /**
   * Get tenant settings
   */
  async getTenantSettings(): Promise<TenantSettings> {
    const { data } = await api.get('/admin/settings');
    return data;
  }

  /**
   * Update tenant settings
   */
  async updateTenantSettings(settings: Partial<TenantSettings>): Promise<void> {
    await api.put('/admin/settings', settings);
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(): Promise<any[]> {
    const { data } = await api.get('/admin/activity');
    return data.activity || [];
  }
}

// Create and export singleton instance
const adminService = new AdminService();

export default adminService;
